import React, { useState, useContext, useEffect, useRef } from "react";
import {
  Box,
  Button,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useTheme } from "@emotion/react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { AuthContext } from "../Auth/Auth";
import { checkIfUserIsAdminForClub } from "../Helper";

export default function ExistingClubProject({ projectType, handleCreateTeam, getExistingTeamNames }) {
  const { currentUser } = useContext(AuthContext);
  const { clubId } = useParams();
  console.log("useParams", useParams());
  const theme = useTheme();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [errors, setErrors] = useState({});
  const teamValues = useRef({ name: "", members: [] });

  const [isCurrentUserAdmin, setIsCurrentUserAdmin] = useState(false);

  useEffect(() => {
    const fetchAdminStatus = async () => {
      try {
        const isAdmin = await checkIfUserIsAdminForClub(clubId, currentUser.uid);
        setIsCurrentUserAdmin(isAdmin);
      } catch (error) {
        console.error("Error checking admin status:", error);
        setIsCurrentUserAdmin(false);
      }
    };

    if (clubId && currentUser?.uid) {
      fetchAdminStatus();
    }
  }, [clubId, currentUser.uid]);


  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const membershipsQuery = query(
          collection(db, "memberships"),
          where("clubId", "==", clubId)
        );
        const membershipsSnap = await getDocs(membershipsQuery);
        const memberIds = membershipsSnap.docs
          .map(doc => doc.data().user)
          .filter(id => id !== currentUser?.uid); // ✅ fixed here

        if (!memberIds.length) return;

        // ✅ FIX: batch "in" queries (Firestore limit = 30)
        const chunkSize = 30;

        const chunkArray = (arr, size) => {
          const result = [];
          for (let i = 0; i < arr.length; i += size) {
            result.push(arr.slice(i, i + size));
          }
          return result;
        };

        const memberChunks = chunkArray(memberIds, chunkSize);

        const userSnaps = await Promise.all(
          memberChunks.map(chunk =>
            getDocs(
              query(
                collection(db, "users"),
                where("uid", "in", chunk)
              )
            )
          )
        );

        const fetchedMembers = userSnaps.flatMap(snap =>
          snap.docs.map(doc => ({
            id: doc.data().uid,
            name: doc.data().name || doc.data().uid,
          }))
        );

        setMembers(fetchedMembers);
      } catch (error) {
        console.error("Error fetching members:", error);
      }
    };

    if (clubId) fetchMembers();
  }, [clubId, currentUser]);

  const handleOpen = () => {
    teamValues.current = { name: "", members: [] };
    setErrors({});
    setDialogOpen(true);
  };

  const handleClose = () => setDialogOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    teamValues.current[name] = value;
    setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handleMemberSelection = (e) => {
    teamValues.current.members = e.target.value;
    setErrors(prev => ({ ...prev, members: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    const { name, members } = teamValues.current;

    if (!name) newErrors.name = "Team Name is required";
    if (getExistingTeamNames()?.includes(name)) newErrors.name = "Team Name already exists";
    if (projectType !== "Individual" && members.length === 0) newErrors.members = "Select at least one member";

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validateForm();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
    } else {
      handleCreateTeam(teamValues.current);
      setDialogOpen(false);
    }
  };

  return (
    <Box textAlign="center">
      {isCurrentUserAdmin === true ? (
      <Button variant="contained" onClick={handleOpen}>
        Create Team for Participation
      </Button>
      ) : null}
      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        maxWidth="sm"
        disableEscapeKeyDown
        PaperProps={{ component: "form", onSubmit: handleSubmit }}
      >
        <DialogTitle>Create your Team</DialogTitle>

        <DialogContent sx={{ minWidth: 600 }}>
          <TextField
            fullWidth
            name="name"
            label="Team Name"
            margin="normal"
            defaultValue=""
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
          />

            <TextField
              fullWidth
              select
              name="members"
              label="Choose Members"
              margin="normal"
              value={teamValues.current.members}
              onChange={handleMemberSelection}
              SelectProps={{ multiple: true }}
              error={!!errors.members}
              helperText={errors.members || `${teamValues.current.members.length} member(s) selected`}
            >
              {members.length > 0 ? (
                members.map(member => (
                  <MenuItem
                    key={member.id}
                    value={member.id}
                    style={{
                      fontWeight: teamValues.current.members.includes(member.id)
                        ? theme.typography.fontWeightMedium
                        : theme.typography.fontWeightRegular,
                    }}
                  >
                    {member.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No members found</MenuItem>
              )}
            </TextField>
          
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="contained">Submit</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}